import React, { useEffect, useState } from 'react';
import { ShoppingBag, Plus, Pencil, Trash2, Loader2, Package, Truck, X, ArrowDownToLine, Link as LinkIcon } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import ImageField from '../components/ImageField';
import { ShopAnalytics } from '../components/ShopAnalytics';

const money = (c) => `$${((c || 0) / 100).toFixed(2)}`;
const toCents = (v) => Math.round(parseFloat(v || 0) * 100) || 0;
const toDollars = (c) => ((c || 0) / 100).toFixed(2);
const CATEGORIES = ['Membership', 'Donation', 'Events', 'Merchandise'];
const imageIdFrom = (url) => { const m = /\/api\/images\/([A-Za-z0-9_]+)/.exec(url || ''); return m ? m[1] : null; };

const EMPTY = { name: '', slug: '', description: '', images: [], price_public: '', price_member: '', postage: '', stock: 0, active: true, category: 'Merchandise', track_stock: true, custom_amount: false };

export default function ShopAdminTab() {
  const { adminGetProducts, adminCreateProduct, adminUpdateProduct, adminDeleteProduct, adminGetOrders, shopLikesReport, adminMediaByImage } = useAuth();
  const [view, setView] = useState('products'); // products | orders | likes
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [likes, setLikes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null); // null | product-in-edit
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const load = () => {
    setLoading(true);
    adminGetProducts().then((p) => setProducts(Array.isArray(p) ? p : [])).catch(() => setProducts([])).finally(() => setLoading(false));
  };
  const loadOrders = () => { adminGetOrders().then((o) => setOrders(Array.isArray(o) ? o : [])).catch(() => setOrders([])); };
  const loadLikes = () => { shopLikesReport().then(setLikes).catch(() => setLikes({ products: [], timeline: [], total_likes: 0 })); };
  useEffect(() => { load(); loadOrders(); /* eslint-disable-next-line */ }, []);

  const openNew = () => { setErr(''); setForm({ ...EMPTY, images: [] }); };
  const openEdit = (p) => {
    setErr('');
    setForm({
      id: p.id, name: p.name || '', slug: p.slug || '', description: p.description || '',
      images: p.images && p.images.length ? p.images : (p.image_url ? [p.image_url] : []),
      price_public: toDollars(p.price_public), price_member: toDollars(p.price_member),
      postage: toDollars(p.postage), stock: p.stock || 0, active: p.active !== false,
      category: p.category || 'Merchandise', track_stock: p.track_stock !== false, custom_amount: !!p.custom_amount,
    });
  };

  const copyAiInfo = async () => {
    const id = imageIdFrom((form.images || []).find((u) => imageIdFrom(u)));
    if (!id) { toast.error('Add a gallery image first — image details come from the gallery.'); return; }
    try {
      const m = await adminMediaByImage(id);
      if (!m.description && !m.title) { toast.info('That image has no image description yet. Regenerate it in the Gallery editor.'); return; }
      setForm((f) => ({ ...f, description: m.description || f.description, name: f.name.trim() ? f.name : (m.title || f.name) }));
      toast.success('Copied the image\'s image description into the product.');
    } catch { toast.error('No gallery record found for this image.'); }
  };

  const save = async () => {
    if (!form.name.trim()) { setErr('Product name is required.'); return; }
    setBusy(true); setErr('');
    const imgs = (form.images || []).filter(Boolean);
    const payload = {
      name: form.name.trim(), slug: form.slug || '', description: form.description.trim(),
      images: imgs, image_url: imgs[0] || '',
      price_public: toCents(form.price_public), price_member: toCents(form.price_member || form.price_public),
      postage: toCents(form.postage), stock: parseInt(form.stock, 10) || 0, active: !!form.active,
      category: form.category || 'Merchandise', track_stock: !!form.track_stock, custom_amount: form.category === 'Donation' && !!form.custom_amount,
    };
    try {
      if (form.id) await adminUpdateProduct(form.id, payload);
      else await adminCreateProduct(payload);
      setForm(null); load();
    } catch (e) {
      setErr((e.response && e.response.data && e.response.data.detail) || 'Could not save the product.');
    } finally { setBusy(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"? This cannot be undone.`)) return;
    setBusy(true);
    try { await adminDeleteProduct(p.id); load(); } finally { setBusy(false); }
  };

  return (
    <div className="max-w-4xl" data-testid="shop-admin-panel">
      <div className="flex items-center gap-2 mb-1">
        <ShoppingBag size={18} style={{ color: 'var(--maroon)' }} />
        <h2 className="font-title text-xl" style={{ color: 'var(--maroon)' }}>Shop</h2>
      </div>
      <p className="text-sm mb-4" style={{ color: 'var(--olive)' }}>
        Manage everything that can be paid for: membership dues, donations, event tickets and merchandise. Items appear in the Shop
        and on the Support Us page grouped by category. Set a public price and an optional lower member price
        (applied automatically for financial, committee and admin members), stock levels and per-item postage.
      </p>

      <div className="flex gap-2 mb-4">
        <button onClick={() => setView('products')} data-testid="shop-view-products"
          className="px-3 py-1.5 rounded text-sm font-title" style={{ background: view === 'products' ? 'var(--maroon)' : 'var(--sage)', color: view === 'products' ? 'var(--cream)' : 'var(--ink)' }}>Products</button>
        <button onClick={() => { setView('orders'); loadOrders(); }} data-testid="shop-view-orders"
          className="px-3 py-1.5 rounded text-sm font-title" style={{ background: view === 'orders' ? 'var(--maroon)' : 'var(--sage)', color: view === 'orders' ? 'var(--cream)' : 'var(--ink)' }}>Orders</button>
        <button onClick={() => setView('analytics')} data-testid="shop-view-analytics"
          className="px-3 py-1.5 rounded text-sm font-title" style={{ background: view === 'analytics' ? 'var(--maroon)' : 'var(--sage)', color: view === 'analytics' ? 'var(--cream)' : 'var(--ink)' }}>Sales analytics</button>
        <button onClick={() => { setView('likes'); loadLikes(); }} data-testid="shop-view-likes"
          className="px-3 py-1.5 rounded text-sm font-title" style={{ background: view === 'likes' ? 'var(--maroon)' : 'var(--sage)', color: view === 'likes' ? 'var(--cream)' : 'var(--ink)' }}>Likes report</button>
      </div>

      {view === 'products' && (
        <>
          <div className="mb-3">
            <button onClick={openNew} data-testid="shop-add-product-btn"
              className="inline-flex items-center gap-1 px-3 py-2 rounded text-sm font-title" style={{ background: 'var(--maroon)', color: 'var(--cream)' }}>
              <Plus size={15} /> Add product
            </button>
          </div>

          <div className="bg-white rounded-lg shadow-sm border overflow-x-auto" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
            <table className="w-full text-sm" data-testid="shop-products-table">
              <thead><tr style={{ background: 'var(--navy)' }}>
                <th className="px-3 py-2.5 text-left font-title" style={{ color: 'var(--cream)' }}>Product</th>
                <th className="px-3 py-2.5 text-left font-title" style={{ color: 'var(--cream)' }}>Public</th>
                <th className="px-3 py-2.5 text-left font-title" style={{ color: 'var(--cream)' }}>Member</th>
                <th className="px-3 py-2.5 text-left font-title" style={{ color: 'var(--cream)' }}>Postage</th>
                <th className="px-3 py-2.5 text-left font-title" style={{ color: 'var(--cream)' }}>Stock</th>
                <th className="px-3 py-2.5 text-left font-title" style={{ color: 'var(--cream)' }}>Active</th>
                <th className="px-3 py-2.5 text-right font-title" style={{ color: 'var(--cream)' }}>Actions</th>
              </tr></thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={7} className="px-3 py-4 text-center"><Loader2 className="animate-spin inline" size={16} /></td></tr>
                ) : products.length === 0 ? (
                  <tr><td colSpan={7} className="px-3 py-4 text-center" style={{ color: 'var(--olive)' }}>No products yet.</td></tr>
                ) : products.map((p) => (
                  <tr key={p.id} className="border-t" style={{ borderColor: 'rgba(0,0,0,0.06)' }} data-testid={`shop-product-row-${p.id}`}>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        {p.image_url ? <img src={p.image_url} alt="" className="w-9 h-9 rounded object-cover" /> : <div className="w-9 h-9 rounded" style={{ background: 'var(--sage)' }} />}
                        <span className="font-title" style={{ color: 'var(--navy)' }}>{p.name}</span>
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full" style={{ background: 'var(--sage)' }} data-testid={`shop-category-${p.id}`}>{p.category || 'Merchandise'}</span>
                      </div>
                      {p.slug && (
                        <button type="button" onClick={() => { navigator.clipboard?.writeText(`${window.location.origin}/shop/${p.slug}`); toast.success(`Link copied: ${window.location.host}/shop/${p.slug}`); }}
                          className="mt-1 inline-flex items-center gap-1 text-[11px] underline" style={{ color: 'var(--olive)' }} title="Copy the speakable link" data-testid={`shop-product-link-${p.id}`}>
                          <LinkIcon size={11} /> {window.location.host}/shop/{p.slug}
                        </button>
                      )}
                    </td>
                    <td className="px-3 py-2">{money(p.price_public)}</td>
                    <td className="px-3 py-2">{money(p.price_member)}</td>
                    <td className="px-3 py-2">{money(p.postage)}</td>
                    <td className="px-3 py-2">{p.track_stock === false ? '∞' : p.stock}</td>
                    <td className="px-3 py-2">{p.active !== false ? 'Yes' : 'No'}</td>
                    <td className="px-3 py-2 text-right">
                      <button onClick={() => openEdit(p)} title="Edit" data-testid={`shop-edit-${p.id}`} className="mr-2"><Pencil size={15} style={{ color: 'var(--navy)' }} /></button>
                      <button onClick={() => remove(p)} title="Delete" data-testid={`shop-delete-${p.id}`}><Trash2 size={15} style={{ color: '#8a1f11' }} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {view === 'orders' && (
        <div className="bg-white rounded-lg shadow-sm border overflow-x-auto" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
          <table className="w-full text-sm" data-testid="shop-orders-table">
            <thead><tr style={{ background: 'var(--navy)' }}>
              <th className="px-3 py-2.5 text-left font-title" style={{ color: 'var(--cream)' }}>Customer</th>
              <th className="px-3 py-2.5 text-left font-title" style={{ color: 'var(--cream)' }}>Items</th>
              <th className="px-3 py-2.5 text-left font-title" style={{ color: 'var(--cream)' }}>Fulfilment</th>
              <th className="px-3 py-2.5 text-left font-title" style={{ color: 'var(--cream)' }}>Total</th>
              <th className="px-3 py-2.5 text-left font-title" style={{ color: 'var(--cream)' }}>Payment</th>
            </tr></thead>
            <tbody>
              {orders.length === 0 ? (
                <tr><td colSpan={5} className="px-3 py-4 text-center" style={{ color: 'var(--olive)' }}>No orders yet.</td></tr>
              ) : orders.map((o) => (
                <tr key={o.id} className="border-t" style={{ borderColor: 'rgba(0,0,0,0.06)' }} data-testid={`shop-order-row-${o.id}`}>
                  <td className="px-3 py-2"><div className="font-title" style={{ color: 'var(--navy)' }}>{o.name || '—'}</div><div className="text-xs" style={{ color: 'var(--olive)' }}>{o.email}</div></td>
                  <td className="px-3 py-2">{(o.items || []).map((it) => `${it.name} ×${it.qty}`).join(', ')}</td>
                  <td className="px-3 py-2">
                    <span className="inline-flex items-center gap-1">
                      {o.fulfillment === 'delivery' ? <Truck size={14} /> : <Package size={14} />}
                      {o.fulfillment === 'delivery' ? 'Delivery' : 'Pickup'}
                    </span>
                  </td>
                  <td className="px-3 py-2">{money(o.total)}</td>
                  <td className="px-3 py-2">
                    <span className="text-xs px-2 py-0.5 rounded font-title" style={{ background: o.payment_status === 'paid' ? '#1f7a3f' : 'var(--sage)', color: o.payment_status === 'paid' ? '#fff' : 'var(--ink)' }}>
                      {o.payment_status === 'paid' ? 'Paid' : 'Pending'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {view === 'analytics' && <ShopAnalytics />}

      {view === 'likes' && (
        <div data-testid="shop-likes-report">
          {!likes ? <div className="text-center py-6"><Loader2 className="animate-spin inline" size={16} /></div> : (
            <>
              <p className="text-sm mb-3" style={{ color: 'var(--olive)' }}>Views: <strong>{likes.total_views}</strong> · Likes: <strong style={{ color: 'var(--maroon)' }}>{likes.total_likes}</strong> · Purchases: <strong>{likes.total_purchases}</strong></p>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-white rounded-lg shadow-sm border overflow-x-auto" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
                  <table className="w-full text-sm" data-testid="likes-products-table">
                    <thead><tr style={{ background: 'var(--navy)' }}>
                      <th className="px-3 py-2 text-left font-title" style={{ color: 'var(--cream)' }}>Product</th>
                      <th className="px-2 py-2 text-right font-title" style={{ color: 'var(--cream)' }}>Views</th>
                      <th className="px-2 py-2 text-right font-title" style={{ color: 'var(--cream)' }}>Likes</th>
                      <th className="px-2 py-2 text-right font-title" style={{ color: 'var(--cream)' }}>Sold</th>
                      <th className="px-2 py-2 text-center font-title" style={{ color: 'var(--cream)' }}>Movement</th>
                    </tr></thead>
                    <tbody>
                      {(likes.products || []).length === 0 ? <tr><td colSpan={5} className="px-3 py-4 text-center" style={{ color: 'var(--olive)' }}>No products.</td></tr>
                      : likes.products.map((p) => (
                        <tr key={p.id} className="border-t" style={{ borderColor: 'rgba(0,0,0,0.06)' }} data-testid={`likes-row-${p.id}`}>
                          <td className="px-3 py-2" style={{ color: 'var(--navy)' }}>{p.name}</td>
                          <td className="px-2 py-2 text-right">{p.views}</td>
                          <td className="px-2 py-2 text-right" style={{ color: 'var(--maroon)' }}>{p.likes}</td>
                          <td className="px-2 py-2 text-right">{p.purchases}</td>
                          <td className="px-2 py-2 text-center"><span className="text-xs px-2 py-0.5 rounded-full" style={{ background: p.movement === 'fast' ? '#1a7f37' : p.movement === 'slow' ? '#8a1f11' : 'var(--sage)', color: p.movement === 'steady' ? 'var(--ink)' : '#fff' }} data-testid={`movement-${p.id}`}>{p.movement}</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="bg-white rounded-lg shadow-sm border p-3" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
                  <h4 className="font-title text-sm mb-2" style={{ color: 'var(--navy)' }}>Likes over time (monthly)</h4>
                  {(likes.timeline || []).length === 0 ? <p className="text-sm" style={{ color: 'var(--olive)' }}>No like activity yet.</p> : (
                    <table className="w-full text-sm" data-testid="likes-timeline-table">
                      <thead><tr style={{ color: 'var(--olive)' }}><th className="text-left font-title py-1">Month</th><th className="text-right font-title">+Likes</th><th className="text-right font-title">−Unlikes</th><th className="text-right font-title">Net total</th></tr></thead>
                      <tbody>
                        {likes.timeline.map((r) => (
                          <tr key={r.month} className="border-t" style={{ borderColor: 'rgba(0,0,0,0.06)' }}>
                            <td className="py-1">{r.month}</td>
                            <td className="text-right" style={{ color: '#1a7f37' }}>{r.likes}</td>
                            <td className="text-right" style={{ color: '#8a1f11' }}>{r.unlikes}</td>
                            <td className="text-right font-title" style={{ color: 'var(--maroon)' }}>{r.net}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      )}


      {form && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }} data-testid="shop-product-modal">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-5 py-3 border-b" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
              <h3 className="font-title text-lg" style={{ color: 'var(--maroon)' }}>{form.id ? 'Edit product' : 'Add product'}</h3>
              <button onClick={() => setForm(null)} data-testid="shop-modal-close"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="block text-sm font-title mb-1" style={{ color: 'var(--navy)' }}>Name</label>
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="shop-form-name"
                  className="w-full px-3 py-2 rounded border text-sm" style={{ borderColor: 'rgba(0,0,0,0.2)' }} />
              </div>
              <div>
                <label className="block text-sm font-title mb-1" style={{ color: 'var(--navy)' }}>Short link word <span className="text-xs font-normal" style={{ color: 'var(--olive)' }}>— say it as: {window.location.host}/shop/{form.slug || '…'}</span></label>
                <input value={form.slug || ''} onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9]/g, '') })} placeholder="auto from the product name (e.g. poloshirt) — letters & digits only" data-testid="shop-form-slug"
                  className="w-full px-3 py-2 rounded border text-sm" style={{ borderColor: 'rgba(0,0,0,0.2)' }} />
              </div>
              <div>
                <label className="block text-sm font-title mb-1" style={{ color: 'var(--navy)' }}>Category</label>
                <select value={form.category} onChange={(e) => { const c = e.target.value; setForm({ ...form, category: c, track_stock: !(c === 'Membership' || c === 'Donation') }); }} data-testid="shop-form-category"
                  className="w-full px-3 py-2 rounded border text-sm bg-white" style={{ borderColor: 'rgba(0,0,0,0.2)' }}>
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-sm font-title" style={{ color: 'var(--navy)' }}>Description</label>
                  <button type="button" onClick={copyAiInfo} data-testid="shop-copy-ai-desc" title="Copy the image description of the first gallery image into this product"
                    className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border" style={{ borderColor: 'var(--navy)', color: 'var(--navy)' }}><ArrowDownToLine size={12} /> Copy details from image</button>
                </div>
                <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} data-testid="shop-form-desc"
                  className="w-full px-3 py-2 rounded border text-sm" style={{ borderColor: 'rgba(0,0,0,0.2)' }} />
              </div>
              <div>
                <label className="block text-sm font-title mb-1" style={{ color: 'var(--navy)' }}>Images (first is the main image)</label>
                {(form.images || []).map((img, i) => (
                  <div key={i} className="flex items-center gap-2 mb-2" data-testid={`shop-image-row-${i}`}>
                    <div className="flex-1"><ImageField value={img} onChange={(url) => setForm({ ...form, images: form.images.map((x, j) => j === i ? url : x) })} testid={`shop-form-image-${i}`} /></div>
                    <button type="button" onClick={() => setForm({ ...form, images: form.images.filter((_, j) => j !== i) })} data-testid={`shop-image-remove-${i}`}><Trash2 size={15} style={{ color: '#8a1f11' }} /></button>
                  </div>
                ))}
                <button type="button" onClick={() => setForm({ ...form, images: [...(form.images || []), ''] })} data-testid="shop-image-add" className="inline-flex items-center gap-1 px-3 py-1.5 rounded text-sm font-title" style={{ background: 'var(--sage)' }}><Plus size={14} /> Add image</button>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-sm font-title mb-1" style={{ color: 'var(--navy)' }}>Public price ($)</label>
                  <input type="number" step="0.01" min="0" value={form.price_public} onChange={(e) => setForm({ ...form, price_public: e.target.value })} data-testid="shop-form-public"
                    className="w-full px-3 py-2 rounded border text-sm" style={{ borderColor: 'rgba(0,0,0,0.2)' }} />
                </div>
                <div>
                  <label className="block text-sm font-title mb-1" style={{ color: 'var(--navy)' }}>Member price ($)</label>
                  <input type="number" step="0.01" min="0" value={form.price_member} onChange={(e) => setForm({ ...form, price_member: e.target.value })} data-testid="shop-form-member"
                    className="w-full px-3 py-2 rounded border text-sm" style={{ borderColor: 'rgba(0,0,0,0.2)' }} />
                </div>
                <div>
                  <label className="block text-sm font-title mb-1" style={{ color: 'var(--navy)' }}>Postage ($)</label>
                  <input type="number" step="0.01" min="0" value={form.postage} onChange={(e) => setForm({ ...form, postage: e.target.value })} data-testid="shop-form-postage"
                    className="w-full px-3 py-2 rounded border text-sm" style={{ borderColor: 'rgba(0,0,0,0.2)' }} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 items-end">
                <div>
                  <label className="block text-sm font-title mb-1" style={{ color: 'var(--navy)' }}>Stock</label>
                  <input type="number" min="0" value={form.stock} disabled={!form.track_stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} data-testid="shop-form-stock"
                    className="w-full px-3 py-2 rounded border text-sm disabled:opacity-50" style={{ borderColor: 'rgba(0,0,0,0.2)' }} />
                </div>
                <div className="space-y-1 pb-1">
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={form.track_stock} onChange={(e) => setForm({ ...form, track_stock: e.target.checked })} data-testid="shop-form-track-stock" className="accent-[var(--maroon)]" />
                    Track stock (untick for donations / dues)
                  </label>
                  {form.category === 'Donation' && (
                    <label className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={!!form.custom_amount} onChange={(e) => setForm({ ...form, custom_amount: e.target.checked })} data-testid="shop-form-custom-amount" className="accent-[var(--maroon)]" />
                      Donor chooses the amount (min $5)
                    </label>
                  )}
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="shop-form-active" className="accent-[var(--maroon)]" />
                    Active (visible in shop)
                  </label>
                </div>
              </div>
              {err && <p className="text-sm" style={{ color: '#8a1f11' }} data-testid="shop-form-error">{err}</p>}
            </div>
            <div className="flex justify-end gap-2 px-5 py-3 border-t" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
              <button onClick={() => setForm(null)} className="px-4 py-2 rounded text-sm" style={{ background: 'var(--sage)' }}>Cancel</button>
              <button onClick={save} disabled={busy} data-testid="shop-form-save"
                className="inline-flex items-center gap-1 px-4 py-2 rounded text-sm font-title" style={{ background: 'var(--maroon)', color: 'var(--cream)', opacity: busy ? 0.7 : 1 }}>
                {busy && <Loader2 size={14} className="animate-spin" />} Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
